import Swal from "sweetalert2";
import { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import useBrainstormStore from "../../../hooks/useBrainstormStore";
import BrainstormConceptsTemplate from "../../Templates/BrainstormConcepts";
import { generateConceptsByBrainstorm, generateConceptsStream, signImages } from "../../../services/Brainstorm/BrainstormService";

const showErrorToast = (text) => Swal.fire({
    title: 'Ocorreu um erro',
    text,
    timer: 1800,
    showConfirmButton: false,
    background: '#27272A',
    color: '#fff',
    position: 'top-end',
});

/** Formato de conceito no store — compartilhado pelo caminho de streaming e pelo fallback. */
const toStoredConcept = (conceptItem) => ({
    ...conceptItem.concepts,
    image: conceptItem.image,
    signed_image: conceptItem.signed_image,
});

export default function BrainstormConceptsPage() {
    const { projectId } = useParams();
    const location = useLocation();
    const { keycloak } = useKeycloak();
    const KEYCLOACK_TOKEN = keycloak.token;

    const isUpdating = location.state?.isUpdating === true;

    const {
        getBrainstormsByProject,
        getGuidingQuestionsByChallenge,
        getIdeasByGuidingQuestion,
        updateIdeaInGuidingQuestion
    } = useBrainstormStore();

    const [loadingConcepts, setIsLoadingConcepts] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [pendingIdeaIds, setPendingIdeaIds] = useState(() => new Set());
    // Map, não Set: os detalhes da falha (motivo, mensagem, recuperabilidade)
    // viajam com o id em vez de viver numa estrutura paralela que diverge.
    const [failedIdeas, setFailedIdeas] = useState(() => new Map());
    const [conceptsVersion, setConceptsVersion] = useState(0);

    // Impede que uma re-execução do efeito (o token do Keycloak muda ao renovar)
    // dispare um segundo stream para as mesmas ideias.
    const startedForProjectRef = useRef(null);
    const abortRef = useRef(null);
    // Ideias com retry em voo. Um ref, não estado: o guard precisa valer já na
    // segunda chamada síncrona, antes de qualquer re-render.
    const retryingIdeaIdsRef = useRef(new Set());

    // Aborta apenas na desmontagem: abortar no cleanup do efeito de geração mataria
    // o stream a cada renovação de token, e o guard acima impediria o reinício.
    useEffect(() => () => abortRef.current?.abort(), []);

    useEffect(() => {
        const abortController = new AbortController();
        abortRef.current = abortController;

        const signExistingConceptImages = async () => {
            setIsLoadingConcepts(true);
            setHasError(false);

            try {
                const currentBrainstormStore = getBrainstormsByProject(parseInt(projectId));

                if (!currentBrainstormStore || currentBrainstormStore.length === 0) {
                    console.warn('No brainstorm found for project:', projectId);
                    setIsLoadingConcepts(false);
                    return;
                }

                const brainstorm = currentBrainstormStore[0];
                const selectedChallenge = brainstorm.challenges.find(challenge => challenge.selected === true);

                if (!selectedChallenge) {
                    console.warn('No selected challenge found');
                    setIsLoadingConcepts(false);
                    return;
                }

                const challengeGuidingQuestions = getGuidingQuestionsByChallenge(brainstorm.id, selectedChallenge.id);
                const selectedGuidingQuestions = challengeGuidingQuestions.filter(question => question.selected === true);

                if (selectedGuidingQuestions.length === 0) {
                    console.warn('No selected guiding questions found');
                    setIsLoadingConcepts(false);
                    return;
                }

                // Collect ideas with concepts that need signing
                const ideasToSign = [];
                
                selectedGuidingQuestions.forEach(guidingQuestion => {
                    const allIdeas = getIdeasByGuidingQuestion(brainstorm.id, guidingQuestion.id);
                    const selectedIdeas = allIdeas.filter(idea => idea.selected === true);
                    
                    selectedIdeas.forEach(idea => {
                        if (idea.concept?.image) {
                            ideasToSign.push({
                                id: idea.id,
                                guidingQuestionId: guidingQuestion.id,
                                brainstormId: brainstorm.id,
                                concept: idea.concept
                            });
                        }
                    });
                });

                if (ideasToSign.length === 0) {
                    console.log('No ideas with concepts found to sign');
                    setIsLoadingConcepts(false);
                    return;
                }

                const signedResponse = await signImages(ideasToSign, KEYCLOACK_TOKEN);

                // Update each idea with the new signed_image
                if (signedResponse?.ideas && Array.isArray(signedResponse.ideas)) {
                    signedResponse.ideas.forEach((signedIdea) => {
                        updateIdeaInGuidingQuestion(
                            signedIdea.brainstormId,
                            signedIdea.guidingQuestionId,
                            signedIdea.id,
                            { concept: { ...signedIdea.concept } }
                        );
                    });
                    console.log(`Signed ${signedResponse.ideas.length} concept images`);
                }
            } catch (error) {
                setHasError(true);
                Swal.fire({
                    title: 'Ocorreu um erro',
                    text: 'Erro ao assinar imagens dos conceitos. Tente novamente mais tarde',
                    timer: 1800,
                    showConfirmButton: false,
                    background: '#27272A',
                    color: '#fff',
                    position: 'top-end',
                });
                console.error('Error in signExistingConceptImages:', error);
            } finally {
                setIsLoadingConcepts(false);
            }
        };

        const generateConceptsForSelectedIdeas = async () => {
            setIsLoadingConcepts(true);
            setHasError(false);

            try {
                const currentBrainstormStore = getBrainstormsByProject(parseInt(projectId));

                if (!currentBrainstormStore || currentBrainstormStore.length === 0) {
                    console.warn('No brainstorm found for project:', projectId);
                    setIsLoadingConcepts(false);
                    return;
                }

                const brainstorm = currentBrainstormStore[0];
                
                const selectedChallenge = brainstorm.challenges.find(challenge => challenge.selected === true);

                if (!selectedChallenge) {
                    console.warn('No selected challenge found');
                    setIsLoadingConcepts(false);
                    return;
                }

                const challengeGuidingQuestions = getGuidingQuestionsByChallenge(brainstorm.id, selectedChallenge.id);
                const selectedGuidingQuestions = challengeGuidingQuestions.filter(question => question.selected === true);

                if (selectedGuidingQuestions.length === 0) {
                    console.warn('No selected guiding questions found');
                    setIsLoadingConcepts(false);
                    return;
                }

                const selectedIdeasWithContext = [];
                // Correlação por índice: a posição no array enviado identifica a ideia de volta.
                // A chave título|descrição usada antes colidia em ideias duplicadas.
                const ideaIndexMap = [];

                selectedGuidingQuestions.forEach(guidingQuestion => {
                    const allIdeas = getIdeasByGuidingQuestion(brainstorm.id, guidingQuestion.id);
                    const selectedIdeas = allIdeas.filter(idea => idea.selected === true);

                    selectedIdeas.forEach(idea => {
                        // Skip ideas that don't have concepts AND haven't been intentionally deleted
                        if (!idea.concept && !idea.conceptDeleted) {
                            selectedIdeasWithContext.push({
                                title: idea.title,
                                description: idea.description,
                            });

                            ideaIndexMap.push({
                                ideaId: idea.id,
                                brainstormId: brainstorm.id,
                                guidingQuestionId: guidingQuestion.id
                            });
                        }
                    });
                });

                if (selectedIdeasWithContext.length === 0) {
                    console.log('No selected ideas found or all ideas already have concepts');
                    setIsLoadingConcepts(false);
                    return;
                }

                const payload = { ideias: selectedIdeasWithContext };

                setPendingIdeaIds(new Set(ideaIndexMap.map(entry => entry.ideaId)));
                setFailedIdeas(new Map());

                // Rastreio local do que já concluiu: o erro global não traz índice e
                // precisa saber quem ficou para trás sem ler estado dentro de um updater.
                const settledIdeaIds = new Set();

                const clearPending = (ideaId) => {
                    settledIdeaIds.add(ideaId);
                    setPendingIdeaIds(previous => {
                        const next = new Set(previous);
                        next.delete(ideaId);
                        return next;
                    });
                };

                const storeConcept = (index, conceptItem) => {
                    const target = ideaIndexMap[index];
                    if (!target) {
                        console.warn(`Concept received for unknown index: ${index}`);
                        return;
                    }

                    updateIdeaInGuidingQuestion(
                        target.brainstormId,
                        target.guidingQuestionId,
                        target.ideaId,
                        { concept: toStoredConcept(conceptItem) }
                    );
                    clearPending(target.ideaId);
                    setConceptsVersion(version => version + 1);
                };

                let receivedConcepts = 0;

                try {
                    await generateConceptsStream(projectId, payload, {
                        signal: abortController.signal,
                        onConcept: (data) => {
                            receivedConcepts++;
                            storeConcept(data.index, data);
                        },
                        onConceptError: (data) => {
                            const target = ideaIndexMap[data.index];
                            console.warn(
                                `Failed to generate concept for idea index ${data.index}: ${data.error} `
                                + `(reason: ${data.reason ?? 'unknown'}, attempts: ${data.attempts ?? 1})`
                            );
                            if (!target) return;

                            clearPending(target.ideaId);
                            markIdeaFailed(target.ideaId, data);
                            setConceptsVersion(version => version + 1);
                        },
                        onStreamError: (data) => {
                            // O evento global não traz índice: quem ainda estava pendente
                            // nunca vai receber conceito e não pode ficar preso em skeleton.
                            console.warn(`Concept stream failed: ${data?.message}`);

                            const stranded = ideaIndexMap
                                .map(entry => entry.ideaId)
                                .filter(ideaId => !settledIdeaIds.has(ideaId));

                            stranded.forEach(ideaId => clearPending(ideaId));
                            setFailedIdeas(previous => {
                                const next = new Map(previous);
                                stranded.forEach(ideaId => next.set(ideaId, {
                                    reason: 'stream_error',
                                    message: data?.message,
                                    retryable: true,
                                }));
                                return next;
                            });

                            showErrorToast(data?.message || 'Erro ao gerar conceitos. Tente novamente mais tarde');
                            setConceptsVersion(version => version + 1);
                        },
                        onDone: (data) => {
                            if (data.failed > 0) {
                                showErrorToast(`${data.failed} de ${data.total} conceitos não puderam ser gerados.`);
                            }
                            if (data.succeeded === 0) {
                                setHasError(true);
                            }
                        },
                    });
                } catch (streamError) {
                    if (abortController.signal.aborted) {
                        return;
                    }

                    // Streaming indisponível e nada recebido ainda: cai para o endpoint agregado.
                    if (receivedConcepts > 0) {
                        throw streamError;
                    }

                    console.warn('Concept streaming unavailable, falling back to aggregated endpoint:', streamError);

                    const conceptResponse = await generateConceptsByBrainstorm(projectId, KEYCLOACK_TOKEN, payload);

                    if (conceptResponse?.concepts && Array.isArray(conceptResponse.concepts)) {
                        conceptResponse.concepts.forEach((conceptItem, index) => {
                            if (!conceptItem.ideia || !conceptItem.concepts) {
                                console.warn('Invalid concept item structure:', conceptItem);
                                return;
                            }
                            storeConcept(index, conceptItem);
                        });
                    } else {
                        console.warn('Invalid concept response structure or empty concepts array:', conceptResponse);
                    }
                }
            } catch (error) {
                if (abortController.signal.aborted) {
                    return;
                }

                setHasError(true);
                showErrorToast('Erro ao gerar conceitos. Tente novamente mais tarde');
                console.error('Error in generateConceptsForSelectedIdeas:', error);
            } finally {
                // Nenhum skeleton pode sobreviver ao fim da geração, em qualquer desfecho.
                setPendingIdeaIds(new Set());
                setIsLoadingConcepts(false);
            }
        };

        if (isUpdating) {
            signExistingConceptImages();
        } else if (startedForProjectRef.current !== projectId) {
            startedForProjectRef.current = projectId;
            generateConceptsForSelectedIdeas();
        }
    }, [projectId, KEYCLOACK_TOKEN, isUpdating, getBrainstormsByProject, getGuidingQuestionsByChallenge, getIdeasByGuidingQuestion, updateIdeaInGuidingQuestion]);

    /** Localiza em qual questão norteadora a ideia vive, para gravar o conceito de volta. */
    /** `retryable` ausente vale como `true`: o fallback agregado não emite o campo. */
    const markIdeaFailed = (ideaId, data) => setFailedIdeas(previous => {
        const next = new Map(previous);
        next.set(ideaId, {
            reason: data?.reason,
            message: data?.message,
            retryable: data?.retryable !== false,
        });
        return next;
    });

    const findIdeaLocation = (ideaId) => {
        const brainstorm = getBrainstormsByProject(parseInt(projectId))?.[0];
        if (!brainstorm) return null;

        const selectedChallenge = brainstorm.challenges?.find(challenge => challenge.selected === true);
        if (!selectedChallenge) return null;

        const questions = getGuidingQuestionsByChallenge(brainstorm.id, selectedChallenge.id) || [];

        for (const question of questions) {
            const ideas = getIdeasByGuidingQuestion(brainstorm.id, question.id) || [];
            if (ideas.some(idea => idea.id === ideaId)) {
                return { brainstormId: brainstorm.id, guidingQuestionId: question.id };
            }
        }

        return null;
    };

    const handleRetryConcept = async (idea) => {
        if (!idea?.id || retryingIdeaIdsRef.current.has(idea.id)) return;

        const location = findIdeaLocation(idea.id);
        if (!location) return;

        retryingIdeaIdsRef.current.add(idea.id);

        setFailedIdeas(previous => {
            const next = new Map(previous);
            next.delete(idea.id);
            return next;
        });
        setPendingIdeaIds(previous => new Set(previous).add(idea.id));

        const payload = { ideias: [{ title: idea.title, description: idea.description }] };

        try {
            // Rota de streaming mesmo para uma ideia só: é a única com retentativa
            // automática no backend, e o retry manual é onde o rate limit mais aparece.
            let conceptItem = null;
            let conceptFailed = false;

            try {
                await generateConceptsStream(projectId, payload, {
                    onConcept: (data) => { conceptItem = data; },
                    onConceptError: () => { conceptFailed = true; },
                });
            } catch (streamError) {
                // Streaming indisponível e nada recebido: cai para o endpoint agregado,
                // como já faz a geração inicial.
                if (conceptItem || conceptFailed) {
                    throw streamError;
                }

                console.warn('Concept streaming unavailable on retry, falling back to aggregated endpoint:', streamError);

                const conceptResponse = await generateConceptsByBrainstorm(projectId, KEYCLOACK_TOKEN, payload);
                conceptItem = conceptResponse?.concepts?.[0];
            }

            // `concept-error` não traz conceito, então a ausência já sinaliza a falha.
            // Não olhar `conceptFailed` aqui é deliberado: o handler também recebe o
            // evento `error` global, e um conceito já entregue não deve ser descartado.
            if (!conceptItem?.concepts) {
                throw new Error('Resposta sem conceito para a ideia recarregada');
            }

            updateIdeaInGuidingQuestion(
                location.brainstormId,
                location.guidingQuestionId,
                idea.id,
                { concept: toStoredConcept(conceptItem) }
            );
        } catch (error) {
            console.error('Error retrying concept generation:', error);
            markIdeaFailed(idea.id, {});
            showErrorToast('Não foi possível gerar este conceito. Tente novamente.');
        } finally {
            setPendingIdeaIds(previous => {
                const next = new Set(previous);
                next.delete(idea.id);
                return next;
            });
            retryingIdeaIdsRef.current.delete(idea.id);
            setConceptsVersion(version => version + 1);
        }
    };

    return (
        <BrainstormConceptsTemplate
            loadingConcepts={loadingConcepts}
            hasError={hasError}
            pendingIdeaIds={pendingIdeaIds}
            failedIdeas={failedIdeas}
            conceptsVersion={conceptsVersion}
            onRetryConcept={handleRetryConcept}
        />
    );
}